import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { getRetrainCostForRole } from "../Logics/retrainCalculations";
import { getItVendorCost, getItPersonnelCost } from "../Logics/systemCostCalculations";
import { getTotalBenefits, getCostSavingsMinBreakIn, getCostSavingsMaxBreakIn } from "../Logics/benefitCalculations";
import {
  getInflateAdjustedSystemCostMaxBreakIn,
  getInflateAdjustedSystemCostMinBreakIn,
} from "../Logics/costAdjustCalculations";
import {} from "../Logics/summaryHelper";
import { formatNumber } from "../../Util/util";
import _ from "lodash";
import store from "../../Store/configureStore";

// Create styles
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#E4E4E4",
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  section: {
    marginTop: 10,
    paddingTop: 10,
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: "center",
    color: "grey",
  },
  title: {
    fontSize: 24,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 18,
    margin: 12,
    textAlign: "center",
  },
  subtotal: {
    fontSize: 12,
    textAlign: "justify",
    padding: 0,
    marginTop: 10,
    marginBottom: 10,
    fontWeight: 900,
  },
  text: {
    fontSize: 10,
    textAlign: "justify",
    padding: 0,
    marginTop: 3,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableCol: {
    width: "16.7%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    margin: "auto",
    marginTop: 5,
    fontSize: 10,
  },
});

export const SummaryPdf = () => {
  const state = store.getState();

  const renderRetrainingCostHeader = () => (
    <View style={styles.tableRow}>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Occupation</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Number</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Hourly Wage</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Hourly Benefit</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Retraining Hours</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>Subtotal({state.currency.currency})</Text>
      </View>
    </View>
  );

  const renderRetrainingCostRows = () => {
    return state.costs.retrainCostDetails.roles.map((role) => (
      <View style={styles.tableRow}>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{_.get(role, "name")}</Text>
        </View>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{_.get(role, "number")}</Text>
        </View>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{_.get(role, "wage")}</Text>
        </View>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{_.get(role, "benefit")}</Text>
        </View>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{_.get(role, "hours")}</Text>
        </View>
        <View style={styles.tableCol}>
          <Text style={styles.tableCell}>{formatNumber(getRetrainCostForRole(role.role))}</Text>
        </View>
      </View>
    ));
  };
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.header} fixed>
            Created by the ICD-11 Cost Benefit Calculator, for more information please visit
            https://icd11calc.chi-csm.ca
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Retraining Costs</Text>

          <View style={styles.table}>
            {renderRetrainingCostHeader()}
            {renderRetrainingCostRows()}
          </View>
          <Text style={styles.text}>
            Other costs: {formatNumber(state.costs.retrainCostDetails.othercosts)}
            {state.currency.currency}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Productivity Loss Costs</Text>
          <Text style={styles.text}>
            Predicted Initial Coding Time per Inpatient Chart: {_.get(state.costs.prodLossDetails, "coding-time-a")}{" "}
            Minutes
          </Text>
          <Text style={styles.text}>
            Predicted Final Coding Time per Inpatient Chart: {_.get(state.costs.prodLossDetails, "coding-time-b")}{" "}
            Minutes
          </Text>
          <Text style={styles.text}>
            Working Hours In A Day: {_.get(state.costs.prodLossDetails, "working-hours-per-day")} Hours
          </Text>
          <Text style={styles.text}>
            Minimum Break-in Period: {_.get(state.costs.prodLossDetails, "min-break-in-period")} Months
          </Text>
          <Text style={styles.text}>
            Maximum Break-in Period: {_.get(state.costs.prodLossDetails, "max-break-in-period")} Months
          </Text>
          <Text style={styles.text}>
            Working Days per Month: {_.get(state.costs.prodLossDetails, "working-days-per-month")} Days
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>System Change Costs</Text>

          <Text style={styles.text}>
            One Time Software Installation Cost:{" "}
            {formatNumber(_.get(state.costs.systemCostDetails, "one-time-software-installation-cost"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Annual Fee: {formatNumber(_.get(state.costs.systemCostDetails, "annual-fee"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.subtotal}>
            IT Vendor Subtotal: {formatNumber(getItVendorCost())}
            {state.currency.currency}
          </Text>

          <Text style={styles.text}>
            Number of IT Personnel: {_.get(state.costs.systemCostDetails, "number-it-personnel")}
          </Text>
          <Text style={styles.text}>
            Hourly Wage of IT Personnel: {formatNumber(_.get(state.costs.systemCostDetails, "wage-it-personnel"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Hourly Benefits Amount: {formatNumber(_.get(state.costs.systemCostDetails, "hourly-benefit-amount"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>Retraining Hours: {_.get(state.costs.systemCostDetails, "retraining-hours")}</Text>
          <Text style={styles.subtotal}>
            IT Personnel Subtotal: {formatNumber(getItPersonnelCost())}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Other System Costs: {formatNumber(_.get(state.costs.systemCostDetails, "other-system-cost"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.subtotal}>
            Other System Costs Subtotal: {formatNumber(_.get(state.costs.systemCostDetails, "other-system-cost"))}
            {state.currency.currency}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Benefits</Text>
          <Text style={styles.text}>
            Computer Assisted Coding: {formatNumber(_.get(state.benefits.benefitsDetails, "auto-coding"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Vendor Cost Savings: {formatNumber(_.get(state.benefits.benefitsDetails, "vendor-costs-savings"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Travel, Accomodation, and Resource Savings:{" "}
            {formatNumber(_.get(state.benefits.benefitsDetails, "travel-savings"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Accurate Billing for Procedures:{" "}
            {formatNumber(_.get(state.benefits.benefitsDetails, "accurate-billing-saving"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Reduction in Miscoded Reimbursement Claims:{" "}
            {formatNumber(_.get(state.benefits.benefitsDetails, "reduced-claims-savings"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Additional Benefits: {formatNumber(_.get(state.benefits.benefitsDetails, "other-savings"))}
            {state.currency.currency}
          </Text>
          <Text style={styles.subtotal}>
            Total Benefits: {formatNumber(getTotalBenefits())}
            {state.currency.currency}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Adjusted Costs</Text>
          <Text style={styles.text}>Inflation Rate: {_.get(state.costs.costAdjustDetails, "inflation-rate")}%</Text>
          <Text style={styles.text}>
            Years Into the Future: {_.get(state.costs.costAdjustDetails, "years-into-future")} Years
          </Text>
          <Text style={styles.subtotal}>Inflation Adjusted Cost Estimation:</Text>
          <Text style={styles.text}>
            Minimum Break-in Period: {formatNumber(getInflateAdjustedSystemCostMinBreakIn())}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Maximum Break-in Period: {formatNumber(getInflateAdjustedSystemCostMaxBreakIn())}
            {state.currency.currency}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Summary</Text>
          <Text style={styles.text}>
            Total Benefits: {formatNumber(getTotalBenefits())}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Final Total Minimum Cost: {formatNumber(getCostSavingsMinBreakIn())}
            {state.currency.currency}
          </Text>
          <Text style={styles.text}>
            Final Total Maximum Cost: {formatNumber(getCostSavingsMaxBreakIn())}
            {state.currency.currency}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
